import { CardContent, CardHeading, LabeledSwatch } from '@/components/ui'
import { chartColors } from '@/utils'
import { useStatementCoverage } from '../StatementCoverage/useStatementCoverage'
import { ProcessingCard } from './ProcessingCard'

// A stable color per account type so the same type reads the same hue everywhere it's charted.
const ACCOUNT_TYPE_COLOR: Record<string, string> = {
  Checking: chartColors.blue,
  Savings: chartColors.teal,
  'Credit card': chartColors.purple,
  'Debit card': chartColors.sky,
  Loan: chartColors.orange,
  Investment: chartColors.green,
  Retirement: chartColors.lime,
  Other: chartColors.gray,
}

// Same ring geometry as the Coverage card so the two donuts read at the exact same size.
const RING_SIZE = 124
const RING_STROKE = 12
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

// How the case's accounts split across types. One ring segment per type, counting accounts (not
// transactions). Coverage is the source, so cash and brokerage accounts both land here.
export const OverviewAccountMix = () => {
  const coverage = useStatementCoverage()
  const accounts = coverage?.accounts ?? []

  const counts = new Map<string, number>()
  for (const account of accounts) {
    const type = account.type || 'Other'
    counts.set(type, (counts.get(type) ?? 0) + 1)
  }
  const total = accounts.length
  const slices = Array.from(counts.entries())
    .map(([type, count]) => ({
      type,
      count,
      color: ACCOUNT_TYPE_COLOR[type] ?? chartColors.gray,
    }))
    .sort((first, second) => second.count - first.count)

  let offsetBefore = 0
  const segments = slices.map((slice) => {
    const dash = (slice.count / total) * RING_CIRCUMFERENCE
    const segment = { ...slice, dash, offset: offsetBefore }
    offsetBefore += dash
    return segment
  })

  const share = (count: number) =>
    total > 0 ? Math.round((count / total) * 100) : 0

  return (
    <ProcessingCard className="flex flex-col">
      <CardHeading title="Account mix" />
      <CardContent className="flex flex-1 items-start">
        {slices.length === 0 ? (
          <p className="text-sm text-muted-foreground">No accounts yet.</p>
        ) : (
          <div className="flex w-full flex-col items-center gap-4">
            <div
              className="relative shrink-0"
              style={{ width: RING_SIZE, height: RING_SIZE }}
            >
              <svg
                width={RING_SIZE}
                height={RING_SIZE}
                role="img"
                aria-label={`${total} accounts across ${slices.length} types`}
              >
                <circle
                  cx={RING_SIZE / 2}
                  cy={RING_SIZE / 2}
                  r={RING_RADIUS}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={RING_STROKE}
                  className="text-neutral-100"
                />
                {segments.map((segment) => (
                  <circle
                    key={segment.type}
                    cx={RING_SIZE / 2}
                    cy={RING_SIZE / 2}
                    r={RING_RADIUS}
                    fill="none"
                    stroke={segment.color}
                    strokeWidth={RING_STROKE}
                    strokeDasharray={`${segment.dash} ${RING_CIRCUMFERENCE}`}
                    strokeDashoffset={-segment.offset}
                    transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
                  />
                ))}
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
                <span className="text-2xl font-bold text-blue-7 tabular-nums">
                  {total.toLocaleString('en-US')}
                </span>
                <span className="mt-0.5 text-[11px] text-muted-foreground">
                  {total === 1 ? 'account' : 'accounts'}
                </span>
              </div>
            </div>

            <ul className="w-full space-y-2 text-sm">
              {slices.map((slice) => (
                <li key={slice.type}>
                  <LabeledSwatch
                    color={slice.color}
                    label={slice.type}
                    className="gap-2 text-blue-6"
                    value={
                      <>
                        <span className="font-semibold text-blue-7">
                          {slice.count.toLocaleString('en-US')}
                        </span>
                        <span className="ml-2 text-blue-4">
                          {share(slice.count)}%
                        </span>
                      </>
                    }
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </ProcessingCard>
  )
}
